import EzController from 'controllers/ez-base-controller';
import Cookies from 'js-cookie';
import { isNoResultsPage } from 'lib/search-helpers';
import { sendTrackingEvent } from 'components/analytics/conversions/tracking';
import { PARTNER_GRAY_BOX_TRIGGERED, PARTNER_GRAY_BOX_CLICK } from 'components/analytics/conversions/constants/actions';
import PartnerImageResults from 'components/partner/partner-image-results';

export default class PartnerSponsoredResultsController extends EzController {
  static targets = [
    'apiResultsWrapper',
    'couponCodeLink',
  ];

  static values = {
    terms: String,
  };

  noResultTimer = null;

  connect() {
    sendTrackingEvent({
      element: this.element,
      data: {
        action: PARTNER_GRAY_BOX_TRIGGERED,
        label: `Terms-${this.termsValue}`,
        non_interaction: true,
      },
    });

    this.searchTerm = this.apiResultsWrapperTarget.getAttribute('data-keyword');
    this.resourceID = this.apiResultsWrapperTarget.getAttribute('data-resource-id');

    this.impactRadiusCouponCodeUrl = this.element.getAttribute('data-impact-radius-coupon-code-url');
    this.sharedId = this.element.getAttribute('data-sharedid');
    this.subId1 = this.element.getAttribute('data-subid-one');
    this.subId2 = this.element.getAttribute('data-subid-two');
    this.partnerServiceUrl = this.element.getAttribute('data-partner-service-url');

    this.getPartnerImageResults();

    this.noResultTimer = setTimeout(() => {
      if (!this.element.querySelector('.ez-resource-thumb')) {
        this.element.parentNode.removeChild(this.element);
      }
    }, this.noResultsDelay);

    window.addEventListener('resultsLoaded', this.clearNoResultsTimeout);
  }

  disconnect() {
    window.removeEventListener('resultsLoaded', this.clearNoResultsTimeout);
  }

  trackClick(clickSelection) {
    sendTrackingEvent({
      element: this.element,
      data: {
        action: PARTNER_GRAY_BOX_CLICK,
        label: `Terms-${this.termsValue}`,
        partner_click_selection: clickSelection,
      },
    });
  }

  clearNoResultsTimeout = (event) => {
    if (event.detail !== 'END_OF_RESULTS') {
      window.clearTimeout(this.noResultTimer);
    }
  }

  get noResultsDelay() {
    return 15 * 1000;
  }

  get placement() {
    return this.apiResultsWrapperTarget.getAttribute('data-placement');
  }

  get adPlacement() {
    let adPlacementSuffix = document.querySelector('body').dataset.istockAdplacementSuffix;
    if (adPlacementSuffix === undefined) {
      adPlacementSuffix = '';
    }
    if (this.resourceID) return `ADP${adPlacementSuffix}`;

    return isNoResultsPage() ? `NRP${adPlacementSuffix}` : `SRP${adPlacementSuffix}`;
  }

  partnerClick() {
    this.trackClick('Partner-Content');
  }

  revealPromoCode(event) {
    event.preventDefault();

    this.trackClick('Partner-Coupon');

    this.makeCouponWindow();

    window.location = this.makeCouponCodePartnerUrl();
  }

  makeCouponCodePartnerUrl() {
    const params = `sharedid=${this.sharedId}`
                 + '&subId1=coupon'
                 + `&subId2=${this.subId2}`
                 + `&adtype=${this.adType}`
                 + `&adplacement=${this.adPlacement}`
                 + `&subId3=${encodeURI(this.searchTerm)}_CPN_${this.placement}`;

    return `${this.impactRadiusCouponCodeUrl}?${params}`;
  }

  makeCouponWindow() {
    const win = window.open('', 'partner-sale', 'width=520,height=340');

    win.location.href = '/istock-coupons/coupon_code';
  }

  getPartnerImageResults() {
    const partnerImages = new PartnerImageResults(
      this.apiResultsWrapperTarget,
      this.searchTerm,
      this.partnerServiceUrl,
      this.addMoreButton,
    );

    const urlParams = partnerImages.makeUrlParams(
      this.sharedId,
      this.subId1,
      this.subId2,
      this.adType,
      this.adPlacement,
    );

    partnerImages.getResults({
      controllerElem: this.element,
      page: this.apiResultsWrapperTarget.getAttribute('data-page'),
      placement: this.placement,
      affiliate: this.apiResultsWrapperTarget.getAttribute('data-affiliate'),
      apiparams: this.apiResultsWrapperTarget.getAttribute('data-apiparams'),
      impactRadiusUrl: this.apiResultsWrapperTarget.getAttribute('data-impact-radius-url'),
      limit: this.apiResultsWrapperTarget.getAttribute('data-limit'),
      loc: this.apiResultsWrapperTarget.getAttribute('data-loc'),
      rowHeight: this.apiResultsWrapperTarget.getAttribute('data-row-height'),
      maxRows: this.apiResultsWrapperTarget.getAttribute('data-max-rows'),
      urlparams: urlParams,
      padWithTerm: this.apiResultsWrapperTarget.getAttribute('data-pad-with-term'),
    });
  }

  viewMoreClick() {
    this.trackClick('Partner-View More');
  }

  addMoreButton = () => {
    const viewMoreTranslation = this.apiResultsWrapperTarget.getAttribute('data-view-more-text');
    const viewMoreDesc = this.apiResultsWrapperTarget.getAttribute('data-view-more-desc');
    const viewMore = document.querySelector('.ez-resource-grid__view-more');
    if (viewMore) viewMore.remove();

    const li = document.querySelectorAll('ul.ez-resource-grid--partner li');
    for (let i = li.length - 1; i > 0; i -= 1) {
      if (li[i].style.display === 'block') {
        const moreButton = document.createElement('a');
        moreButton.setAttribute('href', this.generateSearchUrl);
        moreButton.classList.add('ez-resource-grid__view-more');
        moreButton.setAttribute('title', viewMoreDesc);
        moreButton.setAttribute('rel', 'nofollow');
        moreButton.setAttribute('target', '_blank');
        moreButton.setAttribute('data-action', 'partner-sponsored-results#viewMoreClick');
        moreButton.innerHTML = `<span>${viewMoreTranslation}</span>`;
        li[i].prepend(moreButton);
        break;
      }
    }
  }

  get generateSearchUrl() {
    if (typeof this.searchTerm === 'undefined' || !this.searchTerm) {
      const searchField = document.querySelector('[data-search-form-target="input"]');
      this.searchTerm = searchField.value;
    }

    const iStockContentType = (this.adType === 'vector') ? 'illustrations' : 'photos';

    const encodedTerm = encodeURI(this.searchTerm.replace(/-/g, ' '));
    let adPlacementSuffix = document.querySelector('body').dataset.istockAdplacementSuffix;
    if (adPlacementSuffix === undefined) {
      adPlacementSuffix = '';
    }
    return `${this.apiResultsWrapperTarget.getAttribute('data-impact-radius-url')}?u=https://www.istockphoto.com/${iStockContentType}/${encodedTerm}&sharedid=www.vecteezy.com&subId1=view-more&subId2=${this.subId2}&adtype=${this.adType}&adplacement=VMB${adPlacementSuffix}&subId3=${this.searchTerm}_view_more_${this.placement}`;
  }

  get adType() {
    return Cookies.get('content_type') || 'vector';
  }
}
